import type { ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { popup as createPopup, tooltip as createTooltip } from 'leaflet';
import type {
  GridLayer,
  HotlineOptions,
  LatLngExpression,
  Layer,
  Popup,
  PopupOptions,
  TileLayerOptions,
  TooltipOptions,
} from 'leaflet';
import type { Feature } from 'geojson';
import { colors } from '@karnott/colors';
import { GOOGLE_MAX_ZOOM, IGN_MAX_ZOOM } from '../constants/mapConstants';
import { PARCELS_CONSTANTS, type ParcelCategory } from '../constants/parcelsConstants';
import type { ClusterId } from '../slices/clusters';
import { spacing } from '../ui/theme';
import { L as Leaflet } from './LeafletOverrides';

type ParcelStyle = {
  color: string;
  fillColor: string;
  fillOpacity: number;
  weight?: number;
};

const defaultStyles: ParcelStyle = {
  color: '#1DD7D9',
  fillColor: '#1DD7D9',
  fillOpacity: 0.2,
};

const anonymousStyles: ParcelStyle = {
  color: '#f67d00',
  fillColor: '#f7931e',
  fillOpacity: 0.2,
  weight: 1,
};

const farmHouseStyles: ParcelStyle = {
  color: '#D8C60A',
  fillColor: '#dbcb22',
  fillOpacity: 0.2,
};

export const hotlineStyles = (min: number, max: number): HotlineOptions => ({
  max,
  min,
  palette:
    min > 0
      ? { 0: '#2DE8EA', 0.1: '#76C22C', 0.99: '#76C22C', 1: '#FF4949' }
      : { 0: '#76C22C', 0.99: '#76C22C', 1: '#FF4949' },

  weight: 3,
  smoothFactor: 1,
  outlineWidth: 0,
});

type BuildParcelStyleParams = {
  category?: ParcelCategory;
  clusterId?: ClusterId;
};

export const buildParcelStyle = ({ category, clusterId }: BuildParcelStyleParams) => {
  if (!clusterId) {
    return anonymousStyles;
  }
  if (category === PARCELS_CONSTANTS.CATEGORY.FARMHOUSE) {
    return farmHouseStyles;
  }
  return defaultStyles;
};

function getStaticHTML(component: ReactNode) {
  const div = document.createElement('div');
  const root = createRoot(div);
  flushSync(() => {
    root.render(component);
  });
  return div.innerHTML;
}

export function openLeafletPopup(
  layer: Layer | undefined,
  content: ReactNode,
  popupOptions: PopupOptions = {},
  position?: LatLngExpression,
) {
  const popup: Popup & { _contentNode?: HTMLElement } = createPopup(popupOptions);

  const html = getStaticHTML(content);

  popup.setContent(html);

  layer && layer.bindPopup(popup).openPopup(position);
  popup._contentNode && hydrateRoot(popup._contentNode, content);
}

export function openLeafletTooltip(
  layer: Layer | undefined,
  content: ReactNode,
  tooltipOptions: TooltipOptions = {},
  position?: LatLngExpression,
) {
  const tooltip = createTooltip(tooltipOptions);

  const html = getStaticHTML(content);

  tooltip.setContent(html);

  layer && layer.bindTooltip(tooltip).openTooltip(position);
}

type MarkerLocation = {
  latitude: number;
  longitude: number;
};

type MarkerCircleStyle = {
  location: MarkerLocation;
  color: string;
  radius: number;
  pane: string;
  renderer: 'svg';
  className?: string;
};

export function getCustomMarkerPositionStyle(location?: MarkerLocation): MarkerCircleStyle[] {
  if (!location) return [];
  return [
    {
      location: location,
      color: 'rgba(249, 227, 201, 0.4)',
      radius: spacing * 1.5,
      pane: 'shadowPane',
      className: 'place-location-marker three',
      renderer: 'svg',
    },
    {
      location: location,
      color: 'rgba(250, 144, 144, 0.8)',
      radius: spacing,
      pane: 'shadowPane',
      className: 'place-location-marker two',
      renderer: 'svg',
    },
    {
      location: location,
      color: colors('red', 'dark'),
      radius: spacing / 2,
      pane: 'shadowPane',
      renderer: 'svg',
    },
  ];
}

type StyledFeature = Feature & { geometry: { properties?: Record<string, unknown> } };

/**
 * Set a selection of features as inert (click-through). `filter` takes a Leaflet GeoJSON feature and returns `true` if
 * the feature should be inert. If `filter` is omitted, then all the features are set as inert.
 */
export function inertFeatures(filter?: (feature: StyledFeature) => boolean) {
  return (feature: StyledFeature) => ({ className: !filter || filter(feature) ? 'not-interactive' : '' });
}

/** Get a new instance of Google mutant layer for Leaflet. The same instance can only be shown once at a time */
export function getGoogleMutantTiles() {
  const gridLayer = Leaflet.gridLayer as typeof Leaflet.gridLayer & {
    googleMutant: (options: { type: 'roadmap' | 'satellite' | 'terrain' | 'hybrid' }) => GridLayer;
  };
  return gridLayer.googleMutant({
    type: 'hybrid',
  });
}

/** Get a new instance of IGN layer for Leaflet. The same instance can only be shown once at a time */
export function getIGNTiles() {
  // Leaflet fills `{format}` in the URL template from the options object, so `format` is not a Leaflet option
  const options: TileLayerOptions & { format: string } = {
    attribution: '<a target="_blank" href="https://www.geoportail.gouv.fr/">Geoportail France</a>',
    minZoom: 0,
    maxZoom: IGN_MAX_ZOOM,
    format: 'image/png',
  };
  return Leaflet.tileLayer(
    'https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2&STYLE=normal&FORMAT={format}&TILEMATRIXSET=PM&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}',
    options,
  );
}

/** Get a new instance of a white tile layer for Leaflet */
export function getEmptyTiles() {
  return Leaflet.tileLayer(
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMgAAADICAIAAAAiOjnJAAAABGdBTUEAALGPC/xhBQAAAAlwSFlzAAAOwgAADsIBFShKgAAAABl0RVh0U29mdHdhcmUAcGFpbnQubmV0IDQuMC4xOdTWsmQAAAJLSURBVHhe7dYxagMxFEXR7H9/BlcmpQsbYohxUsxMXGgLVwFxTqXXXz76OCAgLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiKxbFj7vr9+fsdgumXDejy+T6fLGEy3bFi329f1en/frbGZa9mwns/X+fw5BtOt/Hnftm28mG7lsPhHwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiIhLBLCIiEsEsIiISwSwiJwHH886wTjGFYPAgAAAABJRU5ErkJggg==',
    {
      attribution: '',
      minZoom: 0,
      maxZoom: GOOGLE_MAX_ZOOM,
    },
  );
}
